import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import App from "@/App";
import { applyAccessibilityClasses } from "@/lib/settings";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Apply user accessibility preferences (reduce motion / larger text) before paint
applyAccessibilityClasses();

// --- ChunkLoadError safety net ---------------------------------------------
// When a deploy replaces chunk filenames (hashed by webpack), any browser that
// still has the OLD index.html loaded will try to `import()` chunks that no
// longer exist. The server returns index.html (starts with `<`) → the browser
// throws "SyntaxError: Unexpected token '<'" or "ChunkLoadError".
//
// One-time force-reload on that specific failure gets the user onto the fresh
// bundle without ever showing them a red error screen. A `sessionStorage` flag
// prevents an infinite reload loop if the chunk really is missing (e.g. a
// truly broken deploy).
const RELOAD_FLAG = "adn.chunkReload.v1";
function isChunkLoadError(msg) {
  if (!msg) return false;
  const s = String(msg);
  return (
    s.includes("ChunkLoadError") ||
    s.includes("Loading chunk") ||
    /Unexpected token\s+['"]?</.test(s) ||
    s.includes("Loading CSS chunk")
  );
}
function tryReloadOnce() {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return; // already tried this session
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch (_) { /* private mode */ }
  window.location.reload();
}
window.addEventListener("error", (ev) => {
  if (isChunkLoadError(ev?.message) || isChunkLoadError(ev?.error?.message)) {
    tryReloadOnce();
  }
});
window.addEventListener("unhandledrejection", (ev) => {
  const reason = ev?.reason;
  const msg = reason?.message || (typeof reason === "string" ? reason : "");
  if (isChunkLoadError(msg)) tryReloadOnce();
});
// Clear the guard flag on a fully successful load
window.addEventListener("load", () => {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) { /* ignore */ }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);

// Register the PWA service worker (production-safe; skipped in dev to avoid stale caches)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // Auto-activate updates when a new SW is waiting
        if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
        reg.addEventListener("updatefound", () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener("statechange", () => {
            if (sw.state === "installed" && navigator.serviceWorker.controller) {
              sw.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch(() => {
        /* SW registration failure is non-fatal */
      });

    // When a new SW takes control, reload once so users get fresh assets
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}
